import { useMemo } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ArrowUpRight } from "lucide-react";

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const base = (w) => w.name.split(" (")[0].trim();
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function matchesFor(token, words) {
  const t = token.toLowerCase();
  return words.filter((w) => {
    const b = base(w).toLowerCase();
    return t === b || (t.startsWith(b) && t.length - b.length <= 3) || (b.startsWith(t) && b.length - t.length <= 2 && t.length >= 4);
  });
}

function shortenUsage(usage, max = 8) {
  const items = usage.replace(/\[(idiom|phrase)\]\s*/g, "").split(/,\s*/).filter(Boolean);
  const shown = items.slice(0, max).join(", ").replace(/\.$/, "");
  return items.length > max ? `${shown}, and ${items.length - max} more` : shown;
}

function WordPopover({ token, matches, onExplore }) {
  const w = matches[0];
  const short = w.senses?.length ? w.senses.join(", ") : w.gloss;
  const summary = (w.definition || "").split(/;\s/)[0];
  const kjvShort = w.kjv_usage ? shortenUsage(w.kjv_usage) : null;
  return (
    <HoverCard openDelay={120} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={() => onExplore(w.name)}
          data-testid={`verse-word-${slugify(w.name)}`}
          className="rounded-sm underline decoration-gold decoration-dotted decoration-2 underline-offset-4 transition-colors hover:bg-gold/15 hover:text-foreground"
        >
          {token}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        sideOffset={10}
        collisionPadding={{ top: 84, left: 16, right: 16, bottom: 16 }}
        className="w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl border-border bg-card p-6 font-sans not-italic shadow-xl sm:w-96"
        data-testid={`word-popover-${slugify(w.name)}`}
      >
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-serif text-3xl font-semibold text-foreground">{w.word}</span>
          {w.original && <span className="font-serif text-3xl text-gold">{w.original}</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
          {w.transliteration && <span className="italic">{w.transliteration}</span>}
          {w.pronunciation && <span>· {w.pronunciation}</span>}
          <span className="font-mono text-xs uppercase tracking-wider">
            {w.language && <>· {w.language}</>} {w.strongs && <>· {w.strongs}</>} {!w.parsing && w.morph && <>· {w.morph}</>}
          </span>
        </div>
        {short && (
          <p className="mt-4 text-lg font-medium leading-snug text-foreground" data-testid={`word-short-${slugify(w.name)}`}>
            {short}
          </p>
        )}
        {(w.parsing || w.verse_gloss) && (
          <p className="mt-2 flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground" data-testid={`popover-parsing-${slugify(w.name)}`}>
            {w.parsing && <span className="italic">{w.parsing.replace(/^verb,\s*/i, "")}</span>}
            {w.verse_gloss && <span>· here: <span className="font-medium text-foreground">&ldquo;{w.verse_gloss}&rdquo;</span></span>}
          </p>
        )}
        {summary && (
          <p className={`${short ? "mt-3 text-sm text-muted-foreground" : "mt-4 text-base text-foreground/90"} leading-relaxed`}>
            <span className="font-mono text-xs uppercase tracking-wider text-gold">Strong&apos;s: </span>
            {summary}
          </p>
        )}
        {kjvShort && (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            <span className="font-mono text-xs uppercase tracking-wider text-gold">KJV renders it: </span>
            {kjvShort}
          </p>
        )}
        {matches.length > 1 && (
          <p className="mt-2 font-mono text-xs uppercase tracking-wider text-muted-foreground">
            +{matches.length - 1} more sense{matches.length > 2 ? "s" : ""} in Word Study
          </p>
        )}
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => onExplore(w.name)}
            data-testid={`word-popover-explore-${slugify(w.name)}`}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Explore this word <ArrowUpRight className="h-3 w-3" />
          </button>
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Strong&apos;s Concordance</span>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function VerseWithWords({ text, words = [], tokens = [], onExplore }) {
  const parts = useMemo(() => {
    if (!words.length) return [{ text }];
    if (tokens.length) {
      const byStrongs = {};
      words.forEach((w) => { if (w.strongs) (byStrongs[w.strongs] ||= []).push(w); });
      return tokens.map((t) => (t.strongs && byStrongs[t.strongs] ? { text: t.text, matches: byStrongs[t.strongs] } : { text: t.text }));
    }
    const phrases = words.map(base).filter((b) => b.includes(" ")).sort((a, b) => b.length - a.length);
    const re = new RegExp(`(${[...phrases.map(escape), "[A-Za-z][A-Za-z'’]*"].join("|")})`, "g");
    return text.split(re).filter(Boolean).map((seg) => {
      if (!/^[A-Za-z]/.test(seg)) return { text: seg };
      const matches = matchesFor(seg, words);
      return matches.length ? { text: seg, matches } : { text: seg };
    });
  }, [text, words, tokens]);

  return (
    <p className="font-serif text-2xl italic leading-relaxed text-foreground/90 sm:text-3xl" data-testid="full-verse-text">
      {parts.map((p, i) =>
        p.matches ? <WordPopover key={`${i}-${p.text}`} token={p.text} matches={p.matches} onExplore={onExplore} /> : <span key={`${i}-${p.text}`}>{p.text}</span>
      )}
    </p>
  );
}
